import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowLeft, ArrowUpRight } from 'lucide-react';
import ProductMock from './ProductMock';
import { BRANDS } from '@/data/brands';
import { useBrand } from '@/context/BrandContext';

function BrandCard({ brandKey, brand, onSelect, index }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const ArrowCta = isAr ? ArrowLeft : ArrowRight;
  const meta = t(`brands_section.brands.${brandKey}`, { returnObjects: true }) || {};

  return (
    <motion.button
      type="button"
      onClick={() => onSelect(brandKey)}
      data-testid={`brand-card-${brandKey}`}
      className="dwk-brand-card group text-left w-full"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8 }}
      style={{
        background: `linear-gradient(160deg, ${brand.accentSoft} 0%, rgba(247,248,252,0.9) 60%)`,
        border: '1px solid rgba(16,9,46,0.06)',
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute -inset-1 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700 -z-10"
        style={{ background: `radial-gradient(closest-side, ${brand.accent}55, transparent)` }}
        aria-hidden="true"
      />

      <div className="relative p-5 md:p-6 flex flex-col items-start gap-5 min-h-[280px] md:min-h-[340px]">
        {/* Number */}
        <div className="flex items-center justify-between w-full">
          <span className="text-4xl md:text-5xl font-bold dwk-outline-text font-en leading-none">
            {String(index + 1).padStart(2, '0')}
          </span>
          <motion.div
            className="w-9 h-9 rounded-full grid place-items-center text-white"
            style={{ background: brand.accent }}
            whileHover={{ rotate: 45 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          >
            <ArrowUpRight size={14} />
          </motion.div>
        </div>

        {/* Logo */}
        <div className="w-full h-16 md:h-20 flex items-center">
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={meta.name}
              className={`object-contain ${
                brandKey === 'reanzen'
                  ? 'max-h-[140%] max-w-[90%] scale-[1.35] origin-left'
                  : brandKey === 'brand4'
                  ? 'max-h-[170%] max-w-[95%] scale-[1.45] origin-right mr-2'
                  : brandKey === 'brand3'
                  ? 'max-h-full max-w-[52%] scale-[0.82] origin-left'
                  : 'max-h-full max-w-[70%]'
              }`}
              loading="lazy"
            />
          ) : null}
        </div>

        {/* Copy */}
        <div className="mt-auto">
          <h3 className={`text-xl md:text-2xl font-bold text-[#10092E] tracking-tight ${isAr ? 'font-ar' : 'font-en'}`}>
            {meta.name}
          </h3>
          <p className={`mt-1.5 text-xs md:text-sm text-[#10092E]/60 whitespace-pre-line ${isAr ? 'font-ar' : 'font-en'}`}>
            {meta.tagline}
          </p>
          <p className={`mt-3 text-xs md:text-sm text-[#10092E]/65 leading-relaxed line-clamp-3 ${isAr ? 'font-ar' : 'font-en'}`}>
            {meta.desc}
          </p>

          <div className="mt-4 inline-flex items-center gap-2 text-xs md:text-sm font-semibold" style={{ color: brand.accent }}>
            {t('brands_section.select')} <ArrowCta size={14} />
          </div>
        </div>
      </div>
    </motion.button>
  );
}

// Minimal, crisp South Korea (Taegeukgi) flag as SVG
function KoreaFlag() {
  return (
    <svg width="22" height="16" viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
         className="rounded-[3px] shrink-0 shadow-[0_1px_2px_rgba(16,9,46,0.15)]">
      <rect width="60" height="40" fill="#ffffff" />
      <g transform="translate(30 20)">
        <circle r="8" fill="#C60C30" />
        <path d="M -8 0 A 8 8 0 0 1 8 0 A 4 4 0 0 1 0 0 A 4 4 0 0 0 -8 0 Z" fill="#003478" />
      </g>
      <g fill="#000" transform="translate(9 8)">
        <rect width="10" height="1.6" />
        <rect y="3" width="10" height="1.6" />
        <rect y="6" width="10" height="1.6" />
      </g>
      <g fill="#000" transform="translate(41 25)">
        <rect width="4" height="1.6" /><rect x="6" width="4" height="1.6" />
        <rect y="3" width="4" height="1.6" /><rect x="6" y="3" width="4" height="1.6" />
        <rect y="6" width="4" height="1.6" /><rect x="6" y="6" width="4" height="1.6" />
      </g>
      <g fill="#000" transform="translate(41 8)">
        <rect width="4" height="1.6" /><rect x="6" width="4" height="1.6" />
        <rect y="3" width="10" height="1.6" />
        <rect y="6" width="4" height="1.6" /><rect x="6" y="6" width="4" height="1.6" />
      </g>
      <g fill="#000" transform="translate(9 25)">
        <rect width="10" height="1.6" />
        <rect y="3" width="4" height="1.6" /><rect x="6" y="3" width="4" height="1.6" />
        <rect y="6" width="10" height="1.6" />
      </g>
    </svg>
  );
}

// France (Tricolore) flag
function FranceFlag() {
  return (
    <svg width="22" height="16" viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
         className="rounded-[3px] shrink-0 shadow-[0_1px_2px_rgba(16,9,46,0.15)]">
      <rect width="20" height="40" fill="#0055A4" />
      <rect x="20" width="20" height="40" fill="#FFFFFF" />
      <rect x="40" width="20" height="40" fill="#EF4135" />
    </svg>
  );
}

function ProductScene({ product, index, accent, brandName }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const ArrowCta = isAr ? ArrowLeft : ArrowRight;

  const evenFirst = index % 2 === 0;
  const number = product.number;
  // Prefer real product name from data; fall back to placeholder
  const name = isAr
    ? (product.nameAr || `${t('products.name_prefix')} ${number}`)
    : (product.nameEn || `${t('products.name_prefix')} ${number}`);
  // Prefer product-specific description when provided in brands data
  const rawDesc = isAr
    ? (product.descAr || t('products.placeholder_desc'))
    : (product.descEn || t('products.placeholder_desc'));
  // Break each sentence onto its own line for easier reading.
  const desc = rawDesc.replace(/([.!?؟])\s+(?=\S)/g, '$1\n');

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const productY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const productRot = useTransform(scrollYProgress, [0, 1], [evenFirst ? 4 : -4, evenFirst ? -3 : 3]);
  const productRotY = useTransform(scrollYProgress, [0, 0.5, 1], [evenFirst ? -18 : 18, 0, evenFirst ? 12 : -12]);
  const productRotX = useTransform(scrollYProgress, [0, 0.5, 1], [8, 0, -6]);
  const productScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.86, 1, 0.94]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.4, 0.8, 1], [0, 1, 1, 0]);

  return (
    <section
      ref={sectionRef}
      data-testid={`product-scene-${index + 1}`}
      className="relative py-4 md:py-8"
    >
      <motion.div
        style={{ opacity: bgOpacity }}
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        <div
          className="absolute w-[600px] h-[600px] rounded-full blur-[110px] opacity-40"
          style={{
            top: '20%',
            [evenFirst ? 'right' : 'left']: '-10%',
            background: `radial-gradient(closest-side, ${accent}66, transparent)`,
          }}
        />
        <div
          className="absolute w-[380px] h-[380px] rounded-full blur-[100px] opacity-40"
          style={{
            bottom: '-10%',
            [evenFirst ? 'left' : 'right']: '10%',
            background: 'radial-gradient(closest-side, rgba(137,72,195,0.25), transparent)',
          }}
        />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center">
          {/* Product visual */}
          <motion.div
            className={`lg:col-span-6 relative ${evenFirst ? 'lg:order-2' : 'lg:order-1'}`}
            initial={{ opacity: 0, x: evenFirst ? 80 : -80, filter: 'blur(14px)' }}
            whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              style={{
                y: productY,
                rotate: productRot,
                rotateY: productRotY,
                rotateX: productRotX,
                scale: productScale,
                transformStyle: 'preserve-3d',
                transformPerspective: 1200,
              }}
              className="relative w-full aspect-[3/4] max-w-[480px] mx-auto will-change-transform"
              whileHover={{ scale: 1.05, rotateY: evenFirst ? -8 : 8 }}
              transition={{ type: 'spring', stiffness: 140, damping: 16 }}
            >
              <div className={product.image ? 'dwk-spot-neutral' : 'dwk-spot'} aria-hidden="true" />
              <div className="dwk-floor" aria-hidden="true" />
              <div className="relative z-10 h-full flex items-center justify-center animate-float-slow">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={name}
                    className="w-full h-full object-contain drop-shadow-[0_14px_22px_rgba(16,9,46,0.2)]"
                    loading="lazy"
                  />
                ) : (
                  <ProductMock
                    variant={product.variant}
                    label={brandName.toUpperCase()}
                    badge={isAr ? 'منتج' : 'PRODUCT'}
                    n={number}
                  />
                )}
              </div>
            </motion.div>
          </motion.div>

          {/* Text */}
          <motion.div
            className={`lg:col-span-6 ${evenFirst ? 'lg:order-1' : 'lg:order-2'}`}
            initial={{ opacity: 0, x: evenFirst ? -80 : 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          >
            <div className="flex items-baseline gap-4 mb-4">
              <span className="text-6xl md:text-8xl font-bold dwk-outline-text font-en leading-none">
                {number}
              </span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#10092E]/40 font-en">
                {brandName}
              </span>
            </div>

            <h3
              className={`text-4xl md:text-6xl font-bold tracking-tight text-[#10092E] leading-[1.05] ${
                isAr ? 'font-ar' : 'font-en'
              }`}
              data-testid={`product-name-${index + 1}`}
            >
              {name}
            </h3>

            <p
              className={`mt-6 max-w-lg text-[#10092E]/65 leading-relaxed whitespace-pre-line ${
                isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'
              }`}
            >
              {desc}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Products() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const { activeBrand, setActiveBrand } = useBrand();

  const isActive = activeBrand !== null;
  const activeMeta = activeBrand
    ? t(`brands_section.brands.${activeBrand}`, { returnObjects: true })
    : null;

  const brand = activeBrand ? BRANDS[activeBrand] : null;

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-[#F7F8FC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32">
        <div className="text-[14px] md:text-[15px] tracking-[0.32em] uppercase text-[#8948C3] mb-6 font-semibold" data-testid="products-eyebrow">
          {isActive ? t('products.eyebrow') : t('brands_section.eyebrow')}
        </div>
        <h2
          className={`text-[#10092E] font-bold tracking-tight leading-[1.05] ${
            isAr ? 'font-ar text-5xl md:text-7xl' : 'font-en text-5xl md:text-7xl'
          }`}
          data-testid="products-heading"
        >
          {isActive ? (activeMeta?.name || t('products.heading')) : t('brands_section.heading')}
        </h2>
        <p className={`mt-6 max-w-2xl text-[#10092E]/60 leading-relaxed ${isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'}`}>
          {isActive ? t('products.subheading') : t('brands_section.subheading')}
        </p>
        <div className="hairline mt-14" />
      </div>

      <AnimatePresence mode="wait">
        {!isActive ? (
          <motion.div
            key="brands"
            className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              <BrandCard brandKey="otesaly" brand={BRANDS.otesaly} onSelect={setActiveBrand} index={0} />
              <BrandCard brandKey="reanzen" brand={BRANDS.reanzen} onSelect={setActiveBrand} index={1} />
              <BrandCard brandKey="brand3"  brand={BRANDS.brand3}  onSelect={setActiveBrand} index={2} />
              <BrandCard brandKey="brand4"  brand={BRANDS.brand4}  onSelect={setActiveBrand} index={3} />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={activeBrand}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="sticky top-28 md:top-32 z-40">
              <div className="max-w-7xl mx-auto px-6 md:px-10 mt-8 flex items-center justify-between gap-4 py-2 rounded-2xl">
                <button
                  onClick={() => setActiveBrand(null)}
                  data-testid="brand-back"
                  className={`inline-flex items-center gap-2 rounded-full border border-[#10092E]/15 bg-white/85 backdrop-blur-xl px-4 md:px-5 py-2 md:py-2.5 text-sm md:text-base font-semibold text-[#10092E] hover:border-[#8948C3] hover:text-[#8948C3] hover:-translate-y-0.5 transition-all shadow-[0_8px_24px_-12px_rgba(16,9,46,0.3)] ${isAr ? 'font-ar' : 'font-en'}`}
                >
                  {isAr ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                  {t('brands_section.back')}
                </button>
                {brand.logo ? (
                  <img
                    src={brand.logo}
                    alt={activeMeta?.name}
                    className="h-10 md:h-14 w-auto object-contain opacity-95 rounded-xl bg-white/70 backdrop-blur px-2.5 py-1 border border-[#10092E]/10"
                  />
                ) : (
                  <span
                    className={`hidden md:inline-flex items-center rounded-xl bg-white/70 backdrop-blur px-3 py-1.5 border border-[#10092E]/10 text-sm md:text-base font-semibold text-[#10092E] ${isAr ? 'font-ar' : 'font-en'}`}
                  >
                    {activeMeta?.name}
                  </span>
                )}
              </div>
            </div>

            <div>
              {brand.products.length === 0 ? (
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 md:py-48 text-center">
                  <div className="mx-auto max-w-md">
                    <div className={`text-[11px] tracking-[0.35em] uppercase mb-6 ${isAr ? 'font-ar' : 'font-en'}`}
                         style={{ color: brand.accent }}>
                      {isAr ? 'قريبًا' : 'Coming Soon'}
                    </div>
                    <h3 className={`text-3xl md:text-5xl font-bold text-[#10092E] tracking-tight leading-[1.3] ${isAr ? 'font-ar' : 'font-en'}`}>
                      {isAr
                        ? 'مجموعة ' + (activeMeta?.name || '') + ' قيد الإعداد'
                        : (activeMeta?.name || '') + ' collection is on its way'}
                    </h3>
                    <p className={`mt-6 text-[#10092E]/60 leading-relaxed ${isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'}`}>
                      {isAr
                        ? 'نعمل حاليًا على تجهيز هذه المجموعة. ترقّبوا الكشف قريبًا.'
                        : 'We are curating this collection. Stay tuned for the reveal.'}
                    </p>
                    <button
                      onClick={() => setActiveBrand(null)}
                      className="dwk-btn dwk-btn-ghost mt-10"
                    >
                      {t('brands_section.back')}
                    </button>
                  </div>
                </div>
              ) : (
                brand.products.map((p, i) => (
                  <ProductScene
                    key={p.id}
                    product={p}
                    index={i}
                    accent={brand.accent}
                    brandName={activeMeta?.name || activeBrand}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
